import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatAgent } from '../../interfaces/ChatAgent';
import { ResponseObject } from '../../interfaces/App';
import { getCompanyChatAgents } from '../../services/chatAgentService';
import { useAccessibleAgentsFilter } from '../useAccessibleAgentsFilter';

export const useCompanyChatAgents = (enabled = true) => {
  const { data, isLoading, error, isFetching } = useQuery<
    ResponseObject,
    Error
  >({
    queryKey: ['chat-agents', 'company'],
    queryFn: getCompanyChatAgents,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Don't set refetchOnMount to false - let React Query refetch stale data
    // This allows cache invalidation to work properly
    refetchOnMount: 'always', // Always refetch on mount to pick up invalidated data
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting
  });

  const agents = useMemo(() => {
    return (data?.message as ChatAgent[]) || [];
  }, [data]);

  const { data: accessibleAgents, isAccessLoading } =
    useAccessibleAgentsFilter(agents);

  return {
    data: accessibleAgents,
    isLoading: isLoading || isAccessLoading,
    error,
    isFetching,
  };
};
